import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { useNavigate } from '#hooks/useNavigate';
import { useIsNocturneTheme } from '#style/theme';

type Shortcut = {
  keys: string;
  label: string;
};

function getShortcuts(pathname: string, t: TFunction) {
  if (pathname === '/budget') {
    return [
      { keys: '↑↓', label: t('move') },
      { keys: '↵', label: t('edit') },
      { keys: 'B', label: t('copy last month') },
      { keys: 'Z', label: t('zero') },
      { keys: '/', label: t('search') },
    ];
  }

  if (
    pathname === '/accounts' ||
    pathname.startsWith('/accounts/') ||
    pathname.startsWith('/transactions/')
  ) {
    return [
      { keys: '↑↓', label: t('move') },
      { keys: '↵', label: t('edit') },
      { keys: 'N', label: t('new') },
      { keys: 'C', label: t('clear') },
      { keys: '/', label: t('filter') },
    ];
  }

  if (pathname === '/schedules') {
    return [
      { keys: '↑↓', label: t('move') },
      { keys: '↵', label: t('edit') },
      { keys: 'P', label: t('post now') },
      { keys: 'S', label: t('skip next') },
      { keys: 'N', label: t('new') },
    ];
  }

  if (pathname === '/rules') {
    return [
      { keys: '↑↓', label: t('move') },
      { keys: '↵', label: t('edit') },
      { keys: 'N', label: t('new') },
    ];
  }

  return null;
}

export function NocturneShortcutFooter() {
  const isNocturne = useIsNocturneTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!isNocturne) {
    return null;
  }

  const isFullWidthRoute = ['/reports', '/schedules', '/rules'].some(
    route => pathname === route || pathname.startsWith(`${route}/`),
  );
  const shortcuts: Shortcut[] = getShortcuts(pathname, t) ?? [];
  if (shortcuts.length === 0 && !isFullWidthRoute) {
    return null;
  }
  const navigation = [
    { key: '1', label: t('Budget'), route: '/budget' },
    { key: '2', label: t('Reports'), route: '/reports' },
    { key: '3', label: t('Schedules'), route: '/schedules' },
    { key: '4', label: t('Rules'), route: '/rules' },
  ];

  return (
    <View
      role="contentinfo"
      aria-label={t('Keyboard shortcuts')}
      style={{
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 22.4,
        overflow: 'hidden',
        padding: '11.2px 22.4px',
        borderTop: `1px solid ${theme.tableBorder}`,
        backgroundColor: theme.pageBackground,
        color: theme.pageTextSubdued,
        fontSize: 11,
        whiteSpace: 'nowrap',
      }}
    >
      <View
        style={{
          minWidth: 0,
          flexDirection: 'row',
          gap: 22.4,
          overflow: 'hidden',
        }}
      >
        {shortcuts.map(shortcut => (
          <Text key={`${shortcut.keys}-${shortcut.label}`}>
            <Text
              style={{
                color: theme.sidebarItemTextSelected,
                fontWeight: 500,
              }}
            >
              {shortcut.keys}
            </Text>{' '}
            {shortcut.label}
          </Text>
        ))}
      </View>
      {isFullWidthRoute && (
        <View
          role="navigation"
          aria-label={t('Navigation')}
          style={{
            marginLeft: 'auto',
            flexDirection: 'row',
            gap: 2.8,
          }}
        >
          {navigation.map(item => {
            const isCurrent =
              pathname === item.route || pathname.startsWith(`${item.route}/`);
            return (
              <Button
                key={item.route}
                variant="bare"
                aria-current={isCurrent ? 'page' : undefined}
                onPress={() => void navigate(item.route)}
                style={{
                  padding: '2.8px 5.6px',
                  color: isCurrent
                    ? theme.sidebarItemTextSelected
                    : theme.pageTextSubdued,
                  fontSize: 10,
                }}
              >
                {item.key} {item.label}
              </Button>
            );
          })}
        </View>
      )}
    </View>
  );
}
